import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import Header from '../../components/Header';
import {Footer} from '../../components/Footer';
import {BetSchema, BetsService, MatchWithBetSchema, TournamentPredictionOut, UserScoreSchema} from '../../api';
import {BetResultTooltip} from "../../components/BetResultTooltipProps/BetResultTooltipProps";
import {Skeleton} from "../../components/Skeleton";
import {Rating} from "../../components/Rating";

interface Team {
    id: string;
    name: string;
    logo?: string | null;
}

const MAX_TEAMS_MAP: Record<string, number> = {
    'Групповой этап': 32,
    '1/16 финала': 16,
    '1/8 финала': 8,
    '1/4 финала': 4,
    '1/2 финала': 2,
    'Матч за 3-е место': 1,
    'Финал': 1,
};

const STAGE_MAX_TEAMS: Record<string, number> = {
    'Групповой этап': 32,
    '1/16 финала': 16,
    '1/8 финала': 8,
    '1/4 финала': 4,
    '1/2 финала': 2,
    'Матч за 3-е место': 2,
    'Финал': 2,
};

const UserProfile: React.FC = () => {
    const { userId } = useParams();
    const [profile, setProfile] = useState<UserScoreSchema | null>(null);
    const [bets, setBets] = useState<MatchWithBetSchema[]>([]);
    const [tournament, setTournament] = useState<TournamentPredictionOut[]>([]);
    const [allTeams, setAllTeams] = useState<Record<string, Team>>({});
    const [loading, setLoading] = useState(true);
    const [results, setResults] = useState<Record<string, string[]>>({});

    useEffect(() => {
        const id = parseInt(userId!);
        Promise.all([
            BetsService.userProfile(id),
            BetsService.userBets(id),
            BetsService.userTournament(id),
            BetsService.tournamentData(),
            BetsService.tournamentResults(),  // <-- добавить
        ]).then(([profileData, betsData, tournamentData, tournamentInfo, resultsData]) => {
            setProfile(profileData);
            setBets(betsData);
            setTournament(tournamentData);
            setResults(resultsData as Record<string, string[]>);
            const teamsMap: Record<string, Team> = {};
            for (const t of (tournamentInfo as any).teams) {
                teamsMap[t.id] = t as Team;
            }
            setAllTeams(teamsMap);
        }).finally(() => setLoading(false));
    }, [userId]);

    const formatBetScore = (bet: BetSchema) => {
        const parts: string[] = [`${bet.home}:${bet.away}`];
        if (bet.overtime_home !== null && bet.overtime_away !== null) {
            parts.push(`(${bet.overtime_home}:${bet.overtime_away})`);
        }
        if (bet.penalty_home !== null && bet.penalty_away !== null) {
            parts.push(`пен. ${bet.penalty_home}:${bet.penalty_away}`);
        }
        return parts.join(' ');
    };

    const getMaxTeams = (roundName: string) => MAX_TEAMS_MAP[roundName] || 99;


    if (loading) return <Skeleton />;

    if (!profile) {
        return (
            <>
                <Header />
                <div className='matches'><h2>Пользователь не найден</h2></div>
                <Footer />
            </>
        );
    }

    const STAGE_TITLES: Record<string, string> = {
        'Групповой этап': 'Выход из группы',
        '1/2 финала': 'Полуфинал',
    };

    return (
        <>
            <Header />
            <div className='user-profile'>
                {/* Инфо */}
                <div className='user-profile__header'>
                    {profile.user.avatar && (
                        <img src={profile.user.avatar} alt={profile.user.username} className='user-profile__avatar' />
                    )}
                    <div className='user-profile__info'>
                        <h2 className='user-profile__name'>
                            {profile.user.username}
                            {profile.user.about && <span className='user-profile__about'>{profile.user.about}</span>}
                        </h2>
                        <Rating place={profile} score_only={true} />
                    </div>
                </div>

                {/* Таблица прогнозов */}
                {bets.length > 0 && (
                    <section className='rates'>
                        <h3 className='rates__title'>Прогнозы на матчи</h3>
                        <div className='rates__scroll'>
                            <table className='rates__table'>
                                <thead>
                                    <tr>
                                        <th>Матч</th>
                                        <th className='rates__cell--center'>Счёт</th>
                                        <th className='rates__cell--center'>Прогноз</th>
                                        <th className='rates__cell--right'>Итог</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bets.map((match) => {
                                        const bet = match.bet;
                                        const totalResult = bet ? (bet.classic_points ?? 0) + (bet.stake_profit ?? 0) : 0;

                                        return (
                                            <tr key={match.id}>
                                                <td className='rates__cell--match'>
                                                    <a href={`/matches/${match.id}`} className='rates__match-link'>
                                                        {match.home?.team.name} – {match.away?.team.name}
                                                    </a>
                                                    <div className='rates__cell--small'>{match.round_name}</div>
                                                </td>
                                                <td className='rates__cell--center rates__cell--score'>
                                                    {match.status === 'CLOSED' ? (
                                                        match.home?.overtime_score !== null ? (
                                                            <>
                                                                <div>{match.home?.overtime_score}:{match.away?.overtime_score}</div>
                                                                <div className='rates__cell--small'>осн. {match.home?.score}:{match.away?.score}</div>
                                                                {match.home?.penalty_score !== null && (
                                                                    <div className='rates__cell--small'>пен. {match.home?.penalty_score}:{match.away?.penalty_score}</div>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <>{match.home?.score}:{match.away?.score}</>
                                                        )
                                                    ) : '—'}
                                                </td>
                                                <td className='rates__cell--center'>
                                                    {bet ? formatBetScore(bet) : '—'}
                                                </td>
                                                <td className={`rates__cell--right ${
                                                    bet && match.status === 'CLOSED' ? (totalResult >= 0 ? 'rates__cell--positive' : 'rates__cell--negative') : ''
                                                }`}>
                                                    {bet && match.status === 'CLOSED' ? (
                                                        <BetResultTooltip bet={bet} totalResult={totalResult}>
                                                            {`${totalResult >= 0 ? '+' : ''}${totalResult}`}
                                                        </BetResultTooltip>
                                                    ) : '—'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </section>
                )}

                {/* Турнирные ставки */}
                {tournament.length > 0 && (
                    <section className='user-profile__tournament'>
                        <h3 className='rates__title'>Турнирные прогнозы</h3>
                        {tournament.map((stage) => {
                            const realTeams = results[stage.round_name] || [];
                            const hasResults = realTeams.length > 0;
                            const guessed = stage.selected_team_ids!.filter(id => realTeams.includes(id)).length;
                            const max = getMaxTeams(stage.round_name);
                            const isComplete = realTeams.length >= (STAGE_MAX_TEAMS[stage.round_name] || 99);

                            return (
                                <div key={stage.round_name} className='user-profile__stage'>
                                    <h4 className='user-profile__stage-title'>
                                        {STAGE_TITLES[stage.round_name] || stage.round_name}
                                        {
                                            hasResults ? (
                                                <span className='user-profile__stage-guessed'>
                                                    {guessed}/{stage.selected_team_ids!.length}
                                                    {realTeams.length < (STAGE_MAX_TEAMS[stage.round_name] || 99) && (
                                                        <span className='user-profile__stage-partial'> (промежуточно)</span>
                                                    )}
                                                </span>
                                            ) : (
                                                <span className='tournament__stage-count'>
                                                    {stage.selected_team_ids!.length} / {max}
                                                </span>
                                            )
                                        }
                                    </h4>
                                    {stage.selected_team_ids!.length > 0 && (
                                        <div className='user-profile__teams'>
                                            {stage.selected_team_ids!.map((teamId) => {
                                                const team = allTeams[teamId];
                                                if (!team) return null;
                                                const isCorrect = realTeams.includes(teamId);
                                                return (
                                                    <span key={teamId}
                                                          className={`user-profile__team ${isCorrect ? 'user-profile__team--correct' : isComplete ? 'user-profile__team--wrong' : ''}`}>
                    {team.logo && <img src={team.logo}
                                       alt="" className='user-profile__team-logo'/>}
                                                        {team.name}
                </span>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Невыбранные, но прошедшие */}
                                    {
                                        hasResults &&
                                        realTeams.filter(teamId => !stage.selected_team_ids!.includes(teamId)).length > 0 && (
                                            <div className='user-profile__missed'>
                                                <span className='user-profile__missed-label'>Не угадал:</span>
                                                {realTeams
                                                    .filter(teamId => !stage.selected_team_ids!.includes(teamId))
                                                    .map(teamId => {
                                                        const team = allTeams[teamId];
                                                        if (!team) return null;
                                                        return (
                                                            <span key={teamId} className='user-profile__team user-profile__team--missed'>
                                                                {team.logo && <img src={team.logo} alt="" className='user-profile__team-logo'/>}
                                                                {team.name}
                                                            </span>
                                                        );
                                                    })}
                                            </div>
                                        )
                                    }
                                </div>
                            );
                        })}
                    </section>
                )}
            </div>
            <Footer />
        </>
    );
};
export { UserProfile };
